import { useIonRouter, useIonViewWillEnter } from '@ionic/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FeedbackInviteMode,
  LegacyProductCode,
  ProductForm,
  ProductsApi,
} from '@up2stay/up2promo-api-client';
import type {
  CreateProductDto,
  ProductBehavior,
} from '@up2stay/up2promo-api-client';
import {
  buildInstagramTargetUrl,
  buildProductWriteFromFormValues,
  buildTiktokTargetUrl,
  canSubmitProductForm,
  inferRoutingMode,
  mapLocationsForForm,
  parseInstagramUsername,
  parseTiktokUsername,
  productTypeToBehaviorType,
  type ProductFeedbackQuestionDraft,
  type ProductFormValues,
  type ProductRoutingMode,
} from '@up2stay/up2promo-commons/products';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { isProductHashAlreadyExistsError } from '@/core/components/products/is-product-hash-already-exists-error';
import { mergeProfileLinkCardSetupCopy } from '@/core/components/products/merge-profile-link-card-setup-copy';
import {
  applyProductSetupSuccessLeave,
  resolveProductSetupSuccessNavigation,
} from '@/core/components/products/resolve-product-setup-success-navigation';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useProductScan } from '@/core/hooks/use-product-scan';
import {
  canPopIonHistory,
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import {
  type ProductHashResolution,
  resolveProductHash,
} from '@/core/lib/product-hash-resolution';
import { productsQueryKeys } from '@/core/query-keys/products-query-keys';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { canonicalizeShellPathname, shellPaths } from '@/core/router/app-paths';
import { isSetupDescriptionOptional } from '@/routes/app/products-setup-wizard/products-setup-configure-copy';
import {
  buildConfigurePhases,
  ConfigurePhase,
  type ConfigurePhaseId,
  DestinationChoice,
  type DestinationChoiceId,
  firstConfigurePhase,
  parseSetupTypeParam,
} from '@/routes/app/products-setup-wizard/products-setup-configure-flow';
import { resolveSetupDescription } from '@/routes/app/products-setup-wizard/resolve-setup-description';
import { useProductsSetupNestedLocation } from '@/routes/app/products-setup-wizard/use-products-setup-nested-location';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import { isProductHash } from '@/core/utils/product-link.utils';
import { getApiValidationErrorCode } from '@/core/utils/api-error.utils';
import { isValidSocialUsername } from '@/routes/app/products-setup-wizard/products-setup-validation';

import type { ProductSetupWizardVariant } from './products-setup-wizard-types';

function phaseLabel(
  phase: ConfigurePhaseId,
  setup: Record<string, string | undefined> | undefined,
): string {
  switch (phase) {
    case ConfigurePhase.SelectType:
      return setup?.stepSelectTypeLabel ?? setup?.selectTypeTitle ?? '';
    case ConfigurePhase.Destination:
      return setup?.stepDestinationLabel ?? setup?.destinationTitle ?? '';
    case ConfigurePhase.Location:
      return setup?.stepPickLocationLabel ?? setup?.pickLocationTitle ?? '';
    case ConfigurePhase.CreateLocation:
      return setup?.stepCreateLocationLabel ?? setup?.createLocation ?? '';
    case ConfigurePhase.Username:
      return setup?.stepUsernameLabel ?? setup?.usernameLabel ?? '';
    case ConfigurePhase.CustomUrl:
      return setup?.stepCustomUrlLabel ?? setup?.customUrlLabel ?? '';
    case ConfigurePhase.Profile:
      return setup?.stepProfileLabel ?? setup?.profileStepTitle ?? '';
    case ConfigurePhase.Description:
      return setup?.stepDescriptionLabel ?? setup?.descriptionStepTitle ?? '';
    default: {
      const _exhaustive: never = phase;
      return _exhaustive;
    }
  }
}

function isChoicePhase(phase: ConfigurePhaseId): boolean {
  return (
    phase === ConfigurePhase.SelectType || phase === ConfigurePhase.Destination
  );
}

export function useProductsSetupWizard(
  variant: ProductSetupWizardVariant = 'workspace',
) {
  const isProfileBcMode = variant === 'profile-bc';
  const user = useAuthStore((state) => state.user);
  const profilesLinkCardDict = (
    useDictionary() as {
      profiles?: { linkCard?: Record<string, string | undefined> };
    }
  ).profiles?.linkCard;
  const fallbackPath = isProfileBcMode
    ? shellPaths.profiles
    : shellPaths.moreProducts;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const ionRouter = useIonRouter();
  const backRouter = useIonBackRouter();
  const { navigateShell } = useNavigateShell();
  const navRedirect = (path: string) => {
    ionRouter.push(path, 'root', 'replace');
  };
  const navGoBack = (fallback: string) =>
    goBackOrFallback(backRouter, fallback);
  const hashFromUrl = searchParams.get('hash') ?? '';
  const typeFromParams = parseSetupTypeParam(
    searchParams.get('t'),
    searchParams.get('type'),
  );
  const currentWorkspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [configurePhase, setConfigurePhase] = useState<ConfigurePhaseId>(() =>
    firstConfigurePhase(
      isProfileBcMode ? LegacyProductCode.BC : typeFromParams,
      {
        typeLocked: isProfileBcMode || typeFromParams != null,
        isProfileBcMode,
      },
    ),
  );
  const [destinationChoice, setDestinationChoice] =
    useState<DestinationChoiceId | null>(null);
  const [typeLocked, setTypeLocked] = useState(
    () => isProfileBcMode || typeFromParams != null,
  );
  /** Type chosen on the select-type step (not from QR/DB) — back returns there. */
  const [typePicked, setTypePicked] = useState(false);
  /** Scanned/DB type before the pick, restored when going back to select-type. */
  const [typeBeforePick, setTypeBeforePick] = useState<
    LegacyProductCode | undefined
  >(undefined);
  const [hashes, setHashes] = useState<string[]>(() =>
    hashFromUrl.trim() ? [hashFromUrl.trim()] : [],
  );
  /**
   * Product type from URL (`t` / `type`), scan, or select-type — api-client enum only.
   */
  const [batchProductTypeFromScan, setBatchProductTypeFromScan] = useState<
    LegacyProductCode | undefined
  >(() => (isProfileBcMode ? LegacyProductCode.BC : typeFromParams));
  /**
   * Target kind (behaviour) when user configures it: when product type is
   * unknown or CU. Used for create type; productType is derived via behaviorTypeToProductType.
   */
  const [setupTargetKind, setSetupTargetKind] = useState<
    ProductBehavior | string
  >(() =>
    isProfileBcMode
      ? productTypeToBehaviorType(LegacyProductCode.BC)
      : productTypeToBehaviorType(typeFromParams ?? LegacyProductCode.CU),
  );
  const [setupDescription, setSetupDescription] = useState('');
  const [setupLocationId, setSetupLocationId] = useState<string | null>(null);
  const [setupTargetUrl, setSetupTargetUrl] = useState('');
  const [usernameDraft, setUsernameDraft] = useState('');
  const [setupUserId, setSetupUserId] = useState<string | null>(() =>
    isProfileBcMode ? (user?.id ?? null) : null,
  );
  const [setupRoutingMode, setSetupRoutingMode] =
    useState<ProductRoutingMode | null>(null);
  const [setupFeedbackMode, setSetupFeedbackMode] =
    useState<FeedbackInviteMode>(FeedbackInviteMode.DIRECT_REVIEW);
  const [setupReviewThreshold, setSetupReviewThreshold] = useState(4);
  const [setupFeedbackQuestions, setSetupFeedbackQuestions] = useState<
    ProductFeedbackQuestionDraft[]
  >([]);
  const [showScanModal, setShowScanModal] = useState(false);
  const [pendingResolution, setPendingResolution] =
    useState<ProductHashResolution | null>(null);
  const [isResolvingHash, setIsResolvingHash] = useState(false);
  const [resetConfirmPayload, setResetConfirmPayload] = useState<{
    hash: string;
    productId: string;
    productType?: LegacyProductCode;
  } | null>(null);
  const [showAbortConfirm, setShowAbortConfirm] = useState(false);

  const dict = useDictionary();
  const productSetupCopy = (
    dict as { products?: { setup?: Record<string, string | undefined> } }
  ).products?.setup;
  const productsSetupDict = isProfileBcMode
    ? mergeProfileLinkCardSetupCopy(productSetupCopy, profilesLinkCardDict)
    : (productSetupCopy ?? {});
  const locationWizardDict = dict.locations?.create?.wizard;
  const invalidTargetUrlMessage = (
    dict as { products?: { edit?: { invalidTargetUrl?: string } } }
  ).products?.edit?.invalidTargetUrl;
  const {
    locationName,
    setLocationName,
    locationNameError,
    setLocationNameError,
    locationLatitude,
    setLocationLatitude,
    locationLongitude,
    setLocationLongitude,
    locationAddressDetails,
    setLocationAddressDetails,
    isCreatingLocation,
    resetNestedLocationForm,
    openCreateLocation,
    closeCreateLocation,
    handlePlaceSelect,
    handleCreateLocation,
  } = useProductsSetupNestedLocation({
    workspaceId: currentWorkspace?.id,
    labels: {
      noWorkspace: locationWizardDict?.errors?.noWorkspace,
      nameRequired:
        locationWizardDict?.errors?.nameRequired ??
        productsSetupDict?.locationNameRequired,
      createFailed:
        locationWizardDict?.errors?.createFailed ??
        productsSetupDict?.createLocationError,
      createSuccess:
        productsSetupDict?.createLocationSuccess ??
        locationWizardDict?.success?.title,
    },
    setDirection,
    setConfigurePhase,
    onLocationCreated: setSetupLocationId,
  });
  const { startQrScan, startNfcScan, isQrScanning, isNfcScanning, error } =
    useProductScan({
      emptyCodeMessage: productsSetupDict?.scanEmptyCode,
      invalidCodeMessage: productsSetupDict?.scanInvalidCode,
      scanInstructions: productsSetupDict?.scanQrDescription,
      cameraPermissionMessage: productsSetupDict?.scanCameraErrorPermission,
      nfcAlertMessage: productsSetupDict?.scanNfcDescription,
    });
  const validatedInitialHashRef = useRef(false);
  const createInFlightRef = useRef(false);
  /** Skip first WillEnter — initial useState already hydrates; Ionic re-entry needs reset (DEV-468). */
  const hasSetupViewEnteredRef = useRef(false);
  /** Bumped on re-enter so URL-hash validation re-runs after reset. */
  const [wizardEnterGeneration, setWizardEnterGeneration] = useState(0);
  const profileBcSeed = isProfileBcMode && user?.id ? user.id : null;
  const [appliedProfileBcSeed, setAppliedProfileBcSeed] = useState<
    string | null
  >(null);

  if (profileBcSeed !== appliedProfileBcSeed) {
    setAppliedProfileBcSeed(profileBcSeed);
    if (profileBcSeed) {
      setSetupUserId(profileBcSeed);
      setBatchProductTypeFromScan(LegacyProductCode.BC);
      setSetupTargetKind(productTypeToBehaviorType(LegacyProductCode.BC));
      setTypeLocked(true);
      setConfigurePhase(ConfigurePhase.Profile);
    }
  }

  const hydrateWizardFromUrl = useCallback(() => {
    const trimmedHash = hashFromUrl.trim();
    const locked = isProfileBcMode || typeFromParams != null;
    const initialType = isProfileBcMode ? LegacyProductCode.BC : typeFromParams;

    setCurrentStep(1);
    setDirection(1);
    setDestinationChoice(null);
    setTypeLocked(locked);
    setTypePicked(false);
    setTypeBeforePick(undefined);
    setHashes(trimmedHash ? [trimmedHash] : []);
    setBatchProductTypeFromScan(initialType);
    setSetupTargetKind(
      productTypeToBehaviorType(initialType ?? LegacyProductCode.CU),
    );
    setConfigurePhase(
      firstConfigurePhase(initialType, {
        typeLocked: locked,
        isProfileBcMode,
      }),
    );
    setSetupDescription('');
    setSetupLocationId(null);
    setSetupTargetUrl('');
    setUsernameDraft('');
    setSetupUserId(isProfileBcMode ? (user?.id ?? null) : null);
    setSetupRoutingMode(null);
    setSetupFeedbackMode(FeedbackInviteMode.DIRECT_REVIEW);
    setSetupReviewThreshold(4);
    setSetupFeedbackQuestions([]);
    setShowScanModal(false);
    setPendingResolution(null);
    setIsResolvingHash(false);
    setResetConfirmPayload(null);
    setShowAbortConfirm(false);
    resetNestedLocationForm();
    validatedInitialHashRef.current = false;
    createInFlightRef.current = false;
    setAppliedProfileBcSeed(null);
    setWizardEnterGeneration((generation) => generation + 1);
  }, [
    hashFromUrl,
    isProfileBcMode,
    resetNestedLocationForm,
    typeFromParams,
    user?.id,
  ]);
  const hydrateWizardFromUrlRef = useRef(hydrateWizardFromUrl);

  useEffect(() => {
    hydrateWizardFromUrlRef.current = hydrateWizardFromUrl;
  }, [hydrateWizardFromUrl]);

  useIonViewWillEnter(() => {
    if (!hasSetupViewEnteredRef.current) {
      hasSetupViewEnteredRef.current = true;
      return;
    }
    // Ionic keeps this IonPage mounted in the More tab stack — React state
    // survives leave/re-enter unless we re-hydrate from the current URL.
    hydrateWizardFromUrlRef.current();
  });

  useEffect(() => {
    const trimmed = hashFromUrl.trim();
    if (!trimmed || !currentWorkspace || validatedInitialHashRef.current)
      return;
    validatedInitialHashRef.current = true;
    void (async () => {
      // Same format check as a scan: a typed or old `?hash=` like
      // "qa-test-001" used to pass until the server's 422 at create (DEV-566).
      if (!isProductHash(trimmed)) {
        setHashes([]);
        toast.error(productsSetupDict?.scanInvalidCode);
        return;
      }
      setIsResolvingHash(true);
      try {
        const resolution = await resolveProductHash(
          trimmed,
          currentWorkspace.id,
        );
        if (resolution.kind === 'ready_for_setup') {
          // Keep URL `t` when present; only fill from DB when route has no type.
          if (resolution.productType && !typeFromParams) {
            const parsed = parseSetupTypeParam(resolution.productType);
            if (parsed) {
              setBatchProductTypeFromScan(parsed);
              setSetupTargetKind(productTypeToBehaviorType(parsed));
              if (parsed !== LegacyProductCode.CU) setTypeLocked(true);
            }
          }
          return;
        }
        if (resolution.kind === 'already_in_workspace') {
          setHashes([]);
          if (resolution.productId) {
            setResetConfirmPayload({
              hash: trimmed,
              productId: resolution.productId,
              productType:
                typeFromParams ?? parseSetupTypeParam(resolution.productType),
            });
          } else {
            toast.error(productsSetupDict?.alreadyInWorkspace);
          }
          return;
        }
        setHashes([]);
        setPendingResolution(resolution);
      } finally {
        setIsResolvingHash(false);
      }
    })();
  }, [
    hashFromUrl,
    currentWorkspace,
    typeFromParams,
    productsSetupDict?.alreadyInWorkspace,
    productsSetupDict?.scanInvalidCode,
    user?.id,
    wizardEnterGeneration,
  ]);

  // Keep wizard state aligned when route already has `t` / `type` (deep link, Setup CTA).
  if (
    typeFromParams &&
    batchProductTypeFromScan !== typeFromParams &&
    !isProfileBcMode
  ) {
    setBatchProductTypeFromScan(typeFromParams);
    setSetupTargetKind(productTypeToBehaviorType(typeFromParams));
    setTypeLocked(true);
  }

  // Persist known LegacyProductCode on the route so refresh / back keep Google (etc.) steps.
  useEffect(() => {
    if (isProfileBcMode) return;
    if (!batchProductTypeFromScan) return;
    // Ionic keeps this page mounted while another route is active (e.g. the
    // products list after back) — only write `t` onto the setup route itself.
    if (
      canonicalizeShellPathname(location.pathname) !==
      shellPaths.moreProductsSetup
    ) {
      return;
    }
    // Only persist locked types. A factory CU (open for a pick) or a type picked
    // on the select-type step would otherwise come back as `t` and be forced
    // like a scanned QR type (DEV-456).
    if (!typeLocked || typePicked) return;
    const params = new URLSearchParams(location.search);
    const existing = parseSetupTypeParam(params.get('t'), params.get('type'));
    if (existing === batchProductTypeFromScan) return;
    // Never overwrite a different valid URL type with state.
    if (existing) return;
    params.set('t', batchProductTypeFromScan);
    params.delete('type');
    const query = params.toString();
    navigateShell(query ? `${location.pathname}?${query}` : location.pathname, {
      intent: 'updateQuery',
    });
  }, [
    batchProductTypeFromScan,
    isProfileBcMode,
    location.pathname,
    location.search,
    navigateShell,
    typeLocked,
    typePicked,
  ]);

  const isCreateLocationPhase =
    configurePhase === ConfigurePhase.CreateLocation;
  const needsLocations =
    currentStep >= 2 &&
    (configurePhase === ConfigurePhase.Location || isCreateLocationPhase);
  const { locations } = useLocations(undefined, {
    enabled: needsLocations,
  });

  const useProductTypeFromScan =
    isProfileBcMode ||
    (!!batchProductTypeFromScan &&
      batchProductTypeFromScan !== LegacyProductCode.CU);

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!currentWorkspace || hashes.length === 0)
        throw new Error('Missing workspace or hashes');
      const knownType = isProfileBcMode
        ? LegacyProductCode.BC
        : batchProductTypeFromScan &&
            batchProductTypeFromScan !== LegacyProductCode.CU
          ? batchProductTypeFromScan
          : undefined;
      const formValues = {
        description: setupDescription,
        targetKind: setupTargetKind,
        locationId: setupLocationId,
        targetUrl: setupTargetUrl,
        userId: isProfileBcMode ? (user?.id ?? setupUserId) : setupUserId,
        feedbackMode: setupFeedbackMode,
        reviewThreshold: setupReviewThreshold,
        feedbackQuestions: setupFeedbackQuestions,
      };
      const routingMode =
        setupRoutingMode ?? inferRoutingMode(formValues, knownType);
      const description = resolveSetupDescription({
        description: setupDescription,
        routingMode,
        values: formValues,
        locations: mapLocationsForForm(locations),
        fallback:
          productsSetupDict?.descriptionFallback ??
          productsSetupDict?.step3bTitle ??
          'Product',
      });
      const { label, target, behavior, feedback } =
        buildProductWriteFromFormValues(
          { ...formValues, description },
          knownType,
          routingMode,
        );
      // A printed business card (`?t=BC`) is a PROFILE card; other scan codes
      // only carry the behaviour.
      const printedCard =
        useProductTypeFromScan &&
        batchProductTypeFromScan === LegacyProductCode.BC;
      const baseBody: Omit<CreateProductDto, 'hash'> = {
        label,
        target,
        item: { behavior, ...(printedCard && { form: ProductForm.CARD }) },
        ...(feedback && { feedback }),
      };
      const toCreate = hashes.map((h) => h.trim()).filter(Boolean);
      const results = await Promise.all(
        toCreate.map(async (hash) => {
          return ProductsApi.create({
            path: { workspaceId: currentWorkspace!.id },
            body: { ...baseBody, hash },
          });
        }),
      );
      return results;
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.all(currentWorkspace?.id),
      });
      queryClient.invalidateQueries({
        queryKey: ['physical-business-cards', currentWorkspace?.id],
      });
      const count = results?.length ?? 0;
      toast.success(
        count === 1
          ? productsSetupDict?.setupSuccess
          : productsSetupDict?.setupSuccessMultiple,
      );
      const firstId = (results?.[0] as { data?: { id?: string } } | undefined)
        ?.data?.id;
      const next = resolveProductSetupSuccessNavigation({
        isProfileBcMode,
        firstProductId: firstId,
      });
      // Macrotask: queueMicrotask still runs inside Query's await onSettled and
      // unmounts the wizard before success is committed (error toast).
      window.setTimeout(() => {
        applyProductSetupSuccessLeave(next, {
          canGoBack: canPopIonHistory(backRouter),
          goBack: () => {
            backRouter.goBack();
          },
          navigate: navigateShell,
        });
      }, 0);
    },
    onError: (err: unknown) => {
      createInFlightRef.current = false;
      if (isProductHashAlreadyExistsError(err)) {
        toast.error(
          productsSetupDict?.alreadyConfiguredBlocked ??
            productsSetupDict?.setupError,
        );
        return;
      }
      // Name the field the server rejected instead of a generic failure.
      if (getApiValidationErrorCode(err, 'hash')) {
        toast.error(productsSetupDict?.scanInvalidCode);
        return;
      }
      if (getApiValidationErrorCode(err, 'targetUrl')) {
        toast.error(
          usernameDraft.trim()
            ? productsSetupDict?.usernameInvalid
            : invalidTargetUrlMessage,
        );
        return;
      }
      toast.error(productsSetupDict?.setupError);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: async (payload: {
      productId: string;
      hash: string;
      productType?: LegacyProductCode;
    }) => {
      if (!currentWorkspace) throw new Error('Missing workspace');
      return ProductsApi.remove({
        path: {
          workspaceId: currentWorkspace.id,
          id: payload.productId,
        },
      });
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.all(currentWorkspace?.id),
      });
      const isFirst = hashes.length === 0;
      addHash(payload.hash);
      if (isFirst && payload.productType) {
        setBatchProductTypeFromScan(payload.productType);
        setSetupTargetKind(productTypeToBehaviorType(payload.productType));
        if (payload.productType !== LegacyProductCode.CU) setTypeLocked(true);
        setSetupRoutingMode(null);
        setDestinationChoice(null);
      }
      setResetConfirmPayload(null);
      toast.success(productsSetupDict?.resetSuccess);
    },
    onError: () => {
      toast.error(productsSetupDict?.setupError);
    },
  });

  const addHash = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    setHashes((prev) =>
      prev.some((h) => h.trim() === trimmed) ? prev : [...prev, trimmed],
    );
  };

  const removeHash = (index: number) => {
    setHashes((prev) => prev.filter((_, i) => i !== index));
  };

  const applyReadyForSetup = (
    trimmed: string,
    productTypeRaw?: string | LegacyProductCode,
  ) => {
    const productType = parseSetupTypeParam(
      productTypeRaw == null ? undefined : String(productTypeRaw),
    );
    const isFirst = hashes.length === 0;
    if (
      !isProfileBcMode &&
      !isFirst &&
      productType &&
      batchProductTypeFromScan &&
      productType !== batchProductTypeFromScan
    ) {
      toast.error(productsSetupDict?.differentTypeMessage);
      return false;
    }
    if (
      isProfileBcMode &&
      productType &&
      productType !== LegacyProductCode.BC
    ) {
      toast.error(productsSetupDict?.differentTypeMessage);
      return false;
    }
    addHash(trimmed);
    if (isFirst && productType) {
      const nextType = isProfileBcMode ? LegacyProductCode.BC : productType;
      setBatchProductTypeFromScan(nextType);
      setSetupTargetKind(productTypeToBehaviorType(nextType));
      if (nextType !== LegacyProductCode.CU) setTypeLocked(true);
      setSetupRoutingMode(null);
      setDestinationChoice(null);
    }
    setPendingResolution(null);
    return true;
  };

  const handleScanResult = async (
    result: { hash: string; productType?: string } | null,
  ) => {
    if (!result?.hash?.trim() || !currentWorkspace) {
      if (result && !result.hash.trim()) {
        toast.error(productsSetupDict?.scanEmptyCode);
      }
      setShowScanModal(false);
      return;
    }

    if (!isProductHash(result.hash)) {
      toast.error(productsSetupDict?.scanInvalidCode);
      setShowScanModal(false);
      return;
    }

    const trimmed = result.hash.trim();
    setIsResolvingHash(true);
    setPendingResolution(null);
    try {
      const resolution = await resolveProductHash(trimmed, currentWorkspace.id);
      const productType = result.productType ?? resolution.productType;

      if (resolution.kind === 'ready_for_setup') {
        applyReadyForSetup(trimmed, productType);
        setShowScanModal(false);
        return;
      }

      if (resolution.kind === 'already_in_workspace') {
        if (resolution.productId) {
          setResetConfirmPayload({
            hash: trimmed,
            productId: resolution.productId,
            productType: parseSetupTypeParam(result.productType),
          });
        } else {
          toast.error(productsSetupDict?.alreadyInWorkspace);
        }
        setShowScanModal(false);
        return;
      }

      setPendingResolution({ ...resolution, hash: trimmed });
      setShowScanModal(false);
    } catch {
      toast.error(productsSetupDict?.setupError);
      setShowScanModal(false);
    } finally {
      setIsResolvingHash(false);
    }
  };

  const isNative = isCapacitorNative();

  const handleScanQr = async () => {
    if (isNative && startQrScan) {
      const result = await startQrScan();
      if (result) {
        await handleScanResult(result);
      }
      return;
    }
    setShowScanModal(true);
  };

  const handleScanNfc = async () => {
    if (!startNfcScan) return;
    const result = await startNfcScan();
    if (result) {
      await handleScanResult(result);
    }
  };

  const canProceedFromStep1 =
    hashes.length > 0 &&
    hashes.every((h) => h.trim().length > 0) &&
    !pendingResolution &&
    !isResolvingHash;

  const knownProductTypeForForm =
    batchProductTypeFromScan &&
    batchProductTypeFromScan !== LegacyProductCode.CU
      ? batchProductTypeFromScan
      : undefined;
  const setupFormValues: ProductFormValues = {
    description: setupDescription,
    targetKind: setupTargetKind,
    locationId: setupLocationId,
    targetUrl: setupTargetUrl,
    // Profile-BC locks to self; prefer live auth id so submit stays enabled if
    // state was cleared while the locked label is still shown.
    userId: isProfileBcMode ? (user?.id ?? setupUserId) : setupUserId,
    feedbackMode: setupFeedbackMode,
    reviewThreshold: setupReviewThreshold,
    feedbackQuestions: setupFeedbackQuestions,
  };
  const formLocations = useMemo(
    () => mapLocationsForForm(locations),
    [locations],
  );
  const effectiveSetupRoutingMode =
    setupRoutingMode ??
    inferRoutingMode(setupFormValues, knownProductTypeForForm);
  const canSubmitSetup = canSubmitProductForm({
    description: setupDescription,
    values: setupFormValues,
    knownProductType: knownProductTypeForForm,
    locations: formLocations,
    routingMode: effectiveSetupRoutingMode,
    instagramUsername:
      effectiveSetupRoutingMode === 'instagramUsername'
        ? parseInstagramUsername(setupTargetUrl) || usernameDraft
        : undefined,
    tiktokUsername:
      effectiveSetupRoutingMode === 'tiktokUsername'
        ? parseTiktokUsername(setupTargetUrl) || usernameDraft
        : undefined,
    descriptionOptional: isSetupDescriptionOptional(batchProductTypeFromScan),
  });

  const configurePhases = buildConfigurePhases({
    productType: batchProductTypeFromScan,
    destination: destinationChoice,
    creatingLocation: isCreateLocationPhase,
    typeLocked,
    typePicked,
    isProfileBcMode,
  });

  const goToStep = (next: number) => {
    setDirection(next > currentStep ? 1 : -1);
    setCurrentStep(next);
    if (next < 2) {
      setDestinationChoice(null);
      resetNestedLocationForm();
    }
  };

  const enterConfigure = () => {
    const nextPhase = firstConfigurePhase(batchProductTypeFromScan, {
      typeLocked,
      isProfileBcMode,
    });
    setConfigurePhase(nextPhase);
    setDestinationChoice(null);
    setDirection(1);
    setCurrentStep(2);
  };

  const handleSelectProductType = (type: LegacyProductCode) => {
    if (!typePicked) setTypeBeforePick(batchProductTypeFromScan);
    setTypePicked(true);
    setBatchProductTypeFromScan(type);
    setSetupTargetKind(productTypeToBehaviorType(type));
    setTypeLocked(true);
    setSetupRoutingMode(null);
    setDestinationChoice(null);
    setUsernameDraft('');
    setSetupTargetUrl('');
    setSetupLocationId(null);
    setDirection(1);
    setConfigurePhase(
      firstConfigurePhase(type, { typeLocked: true, isProfileBcMode }),
    );
  };

  const handleChooseDestination = (choice: DestinationChoiceId) => {
    setDestinationChoice(choice);
    setDirection(1);
    if (choice === DestinationChoice.Location) {
      setSetupRoutingMode('location');
      setUsernameDraft('');
      setSetupTargetUrl('');
      setConfigurePhase(ConfigurePhase.Location);
      return;
    }
    if (choice === DestinationChoice.Username) {
      const mode: ProductRoutingMode =
        batchProductTypeFromScan === LegacyProductCode.TT
          ? 'tiktokUsername'
          : 'instagramUsername';
      setSetupRoutingMode(mode);
      setSetupLocationId(null);
      setConfigurePhase(ConfigurePhase.Username);
      return;
    }
    setSetupRoutingMode('customUrl');
    setSetupLocationId(null);
    setUsernameDraft('');
    setConfigurePhase(ConfigurePhase.CustomUrl);
  };

  /** Inline check while typing; the server rejected these only at create. */
  const usernameError =
    usernameDraft.trim() && !isValidSocialUsername(usernameDraft)
      ? productsSetupDict?.usernameInvalid
      : undefined;
  const isUsernamePhase =
    currentStep === 2 && configurePhase === ConfigurePhase.Username;

  const handleUsernameChange = (value: string) => {
    setUsernameDraft(value);
    if (batchProductTypeFromScan === LegacyProductCode.TT) {
      setSetupTargetUrl(buildTiktokTargetUrl(value));
    } else {
      setSetupTargetUrl(buildInstagramTargetUrl(value));
    }
  };

  const handleSwitchToCustomUrl = () => {
    setDestinationChoice(DestinationChoice.CustomUrl);
    setSetupRoutingMode('customUrl');
    setSetupLocationId(null);
    setUsernameDraft('');
    setSetupTargetUrl('');
    setDirection(1);
    setConfigurePhase(ConfigurePhase.CustomUrl);
  };

  const hasProgress =
    hashes.length > 0 || currentStep === 2 || isCreateLocationPhase;
  const handleAbortRequest = () => {
    if (isCreateLocationPhase) {
      closeCreateLocation();
      return;
    }
    if (hasProgress) setShowAbortConfirm(true);
    else navGoBack(fallbackPath);
  };
  const handleAbortConfirm = () => {
    setShowAbortConfirm(false);
    navGoBack(fallbackPath);
  };

  const shellTotalSteps = 1 + configurePhases.length;
  const configurePhaseIndex = Math.max(
    0,
    configurePhases.indexOf(configurePhase),
  );
  const shellCurrentStep = currentStep === 1 ? 1 : 1 + configurePhaseIndex + 1;
  const stepLabels = [
    productsSetupDict?.stepScanLabel ?? productsSetupDict?.step1Title,
    ...configurePhases.map((phase) => phaseLabel(phase, productsSetupDict)),
  ].filter((label): label is string => Boolean(label));

  const shellStepKey =
    currentStep === 1 ? 'scan' : `${configurePhase}:${destinationChoice ?? ''}`;

  const handleShellBack = () => {
    if (isCreateLocationPhase) {
      closeCreateLocation();
      return;
    }
    if (currentStep !== 2) return;

    const idx = configurePhases.indexOf(configurePhase);
    if (idx > 0) {
      const prev = configurePhases[idx - 1];
      if (!prev) return;
      setDirection(-1);
      if (prev === ConfigurePhase.Destination) {
        setDestinationChoice(null);
        setSetupRoutingMode(null);
      }
      if (prev === ConfigurePhase.SelectType) {
        // Undo the pick so the type can be chosen again (DEV-456).
        setTypePicked(false);
        setTypeLocked(false);
        setBatchProductTypeFromScan(typeBeforePick);
        setSetupTargetKind(
          productTypeToBehaviorType(typeBeforePick ?? LegacyProductCode.CU),
        );
        setSetupRoutingMode(null);
        setDestinationChoice(null);
        setUsernameDraft('');
        setSetupTargetUrl('');
        setSetupLocationId(null);
      }
      setConfigurePhase(prev);
      return;
    }

    setSetupRoutingMode(null);
    setDestinationChoice(null);
    goToStep(1);
  };

  const isDescriptionPhase = configurePhase === ConfigurePhase.Description;

  const handleShellNext = () => {
    if (isCreateLocationPhase) {
      void handleCreateLocation();
      return;
    }
    if (currentStep === 1) {
      if (!canProceedFromStep1) return;
      enterConfigure();
      return;
    }
    if (isChoicePhase(configurePhase)) return;
    if (!canSubmitSetup) return;
    if (isUsernamePhase && usernameError) return;

    const idx = configurePhases.indexOf(configurePhase);
    const nextPhase =
      idx >= 0 && idx < configurePhases.length - 1
        ? configurePhases[idx + 1]
        : undefined;

    // Intermediate configure steps (location / username / …) advance to
    // Description; only the description step creates the product(s).
    if (nextPhase && nextPhase !== ConfigurePhase.CreateLocation) {
      setDirection(1);
      setConfigurePhase(nextPhase);
      return;
    }

    if (!createMutation.isPending && !createInFlightRef.current) {
      createInFlightRef.current = true;
      createMutation.mutate();
    }
  };

  const choicePhase = currentStep === 2 && isChoicePhase(configurePhase);

  const nextLabel = isCreateLocationPhase
    ? isCreatingLocation
      ? (productsSetupDict?.creatingLocation ?? locationWizardDict?.creating)
      : (productsSetupDict?.createLocation ??
        locationWizardDict?.createLocation)
    : currentStep === 1 || !isDescriptionPhase
      ? productsSetupDict?.next
      : createMutation.isPending
        ? productsSetupDict?.creating
        : hashes.length > 1
          ? productsSetupDict?.createMultiple
          : productsSetupDict?.create;

  const nextDisabled = isCreateLocationPhase
    ? isCreatingLocation || locationName.trim().length === 0
    : currentStep === 1
      ? !canProceedFromStep1
      : choicePhase
        ? true
        : !canSubmitSetup ||
          createMutation.isPending ||
          (isUsernamePhase && !!usernameError);

  const createLocationLabels = {
    title: productsSetupDict?.createLocationTitle,
    description: productsSetupDict?.createLocationDescription,
    reviewChannelsHint: productsSetupDict?.createLocationReviewHint,
    searchLabel: locationWizardDict?.detailsStep?.searchLabel,
    searchPlaceholder:
      locationWizardDict?.detailsStep?.searchPlaceholder ??
      locationWizardDict?.searchStep?.placeholder,
    searchHint: locationWizardDict?.detailsStep?.searchHint,
    addressLabel: locationWizardDict?.detailsStep?.addressLabel,
    selectedEmptyLabel: locationWizardDict?.detailsStep?.selectedEmptyLabel,
    addressEditHint: locationWizardDict?.detailsStep?.addressEditHint,
    mapPreviewLabel: locationWizardDict?.reviewStep?.mapPreview,
    noCoordinatesLabel: locationWizardDict?.reviewStep?.noCoordinates,
    mapUnavailableLabel: locationWizardDict?.reviewStep?.mapUnavailable,
    dragHintLabel: locationWizardDict?.detailsStep?.dragHint,
  };

  return {
    isProfileBcMode,
    productsSetupDict,
    profilesLinkCardDict,
    fallbackPath,
    currentStep,
    direction,
    hashes,
    removeHash,
    error,
    batchProductTypeFromScan,
    configurePhase,
    destinationChoice,
    usernameDraft,
    setupFormValues,
    handleSelectProductType,
    handleChooseDestination,
    handleUsernameChange,
    usernameError,
    invalidTargetUrlMessage,
    handleSwitchToCustomUrl,
    setSetupLocationId,
    setSetupTargetUrl,
    setSetupUserId,
    setSetupDescription,
    currentWorkspaceId: currentWorkspace?.id,
    formLocations,
    setupRoutingMode,
    setSetupRoutingMode,
    showScanModal,
    setShowScanModal,
    pendingResolution,
    setPendingResolution,
    isResolvingHash,
    resetConfirmPayload,
    setResetConfirmPayload,
    showAbortConfirm,
    setShowAbortConfirm,
    locationName,
    setLocationName,
    locationNameError,
    setLocationNameError,
    locationLatitude,
    locationLongitude,
    setLocationLatitude,
    setLocationLongitude,
    locationAddressDetails,
    setLocationAddressDetails,
    isCreatingLocation,
    isNative,
    startNfcScan,
    isQrScanning,
    isNfcScanning,
    handleScanQr,
    handleScanNfc,
    handleScanResult,
    navRedirect,
    openCreateLocation,
    handlePlaceSelect,
    releaseMutation,
    createMutation,
    isCreateLocationPhase,
    shellCurrentStep,
    shellTotalSteps,
    stepLabels,
    shellStepKey,
    handleShellBack,
    handleShellNext,
    handleAbortRequest,
    handleAbortConfirm,
    nextLabel,
    nextDisabled,
    /** Choice steps advance via in-content buttons — omit shell Next. */
    showShellNext: !choicePhase,
    createLocationLabels,
  };
}

export type UseProductsSetupWizardResult = ReturnType<
  typeof useProductsSetupWizard
>;
